import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { ProjectViewer } from "components/portfolio/project-viewer";

// Shared sections for the redesigned service pages (2D, CGI, Digital Marketing, Game).
// They reuse the 3D Services page's s3d-* styles so every service page feels like one
// family; each page brings its own hero "mock" (artboard, reel, dashboard, game HUD).

const pad = (n) => String(n).padStart(2, "0");

// Muted looping clip that only plays while on screen (saves data and CPU)
export const InViewVideo = ({ src, poster, className = "", label }) => {
  const ref = useRef(null);

  useEffect(() => {
    const video = ref.current;
    if (!video) return undefined;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion || !("IntersectionObserver" in window)) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) video.play().catch(() => {});
        else video.pause();
      },
      { threshold: 0.25 }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  return (
    <video
      ref={ref}
      className={className}
      src={src}
      poster={poster}
      muted
      loop
      playsInline
      preload="none"
      aria-label={label}
    />
  );
};

export const ServiceHero = ({ variant, label, title, highlight, lead, mock, secondary }) => (
  <section className={`s3d-hero svc-hero svc-hero--${variant}`}>
    <div className="svc-hero__bg" aria-hidden="true"></div>
    <div className="container">
      <div className="row align-items-center gy-5">
        <div className="col-lg-5">
          <span className="s3d-label">{label}</span>
          <h1 className="s3d-hero__title">
            {title} <span>{highlight}</span>
          </h1>
          <p className="s3d-hero__lead">{lead}</p>
          <div className="s3d-hero__actions">
            <Link to="/contact-us" className="tp-btnr bg-danger tp-btn-shine-effect">
              Get a Quote
            </Link>
            {secondary && (
              <a href={secondary.href} className="s3d-btn-ghost">
                {secondary.label} <i className="fa-regular fa-arrow-down"></i>
              </a>
            )}
          </div>
        </div>
        <div className="col-lg-7">{mock}</div>
      </div>
    </div>
  </section>
);

// Bento of services. Cards with `image`/`video` are media cards; others are icon cards.
// Service explorer (all service pages): a compact numbered list; the active service drives
// the preview. Hover, focus or tap a row to switch. On phones and tablets the active row shows
// its own image. Items without an image get an icon panel instead of a picture.
export const ServiceBento = ({ label = "What We Do", title, items }) => {
  const [active, setActive] = useState(0);
  const current = items[active];
  const mediaOf = (item) => item.image || item.poster;

  return (
    <section className="s3d-services">
      <div className="container">
        <div className="s3d-head">
          <span className="s3d-label">{label}</span>
          <h2 className="s3d-heading">{title}</h2>
        </div>
        <div className="s3d-explorer">
          <ol className="s3d-explorer__list">
            {items.map((item, i) => (
              <li key={item.title} className={i === active ? "is-active" : ""}>
                <button
                  type="button"
                  className="s3d-explorer__row"
                  onMouseEnter={() => setActive(i)}
                  onFocus={() => setActive(i)}
                  onClick={() => setActive(i)}
                  aria-expanded={i === active}
                >
                  <span className="s3d-explorer__num">{pad(i + 1)}</span>
                  <span className="s3d-explorer__icon">
                    <i className={item.icon}></i>
                  </span>
                  <span className="s3d-explorer__title">{item.title}</span>
                  <i className="fa-regular fa-arrow-right s3d-explorer__arrow"></i>
                </button>
                <div className="s3d-explorer__detail">
                  <div>
                    <p>{item.desc}</p>
                    {item.tags && (
                      <div className="s3d-explorer__tags">
                        {item.tags.map((tag) => (
                          <span key={tag}>{tag}</span>
                        ))}
                      </div>
                    )}
                    {mediaOf(item) && (
                      <img className="s3d-explorer__inline" src={mediaOf(item)} alt={`${item.title} by Rionick Studios`} loading="lazy" />
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ol>
          <div className={`s3d-explorer__preview${mediaOf(current) ? "" : " s3d-explorer__preview--icon"}`} aria-hidden="true">
            {items.map((item, i) =>
              mediaOf(item) ? (
                <img key={item.title} src={mediaOf(item)} alt="" className={i === active ? "is-active" : ""} loading="lazy" />
              ) : null
            )}
            {mediaOf(current) ? (
              <span className="s3d-explorer__caption">
                <i className={current.icon}></i> {current.caption || current.title}
              </span>
            ) : (
              <div className="s3d-explorer__panel" key={current.title}>
                <span className="s3d-explorer__panel-num">{pad(active + 1)}</span>
                <span className="s3d-explorer__panel-icon">
                  <i className={current.icon}></i>
                </span>
                <h3>{current.title}</h3>
                <p>{current.desc}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Horizontal step timeline (the 3D page uses an image pipeline instead)
export const ServiceProcess = ({ label = "How We Work", title, steps }) => (
  <section className="svc-process">
    <div className="container">
      <div className="s3d-head s3d-head--light">
        <span className="s3d-label">{label}</span>
        <h2 className="s3d-heading">{title}</h2>
      </div>
      <ol className="svc-process__steps" style={{ "--steps": steps.length }}>
        {steps.map((step, i) => (
          <li key={step.title}>
            <div className="svc-process__marker">
              <span className="svc-process__icon">
                <i className={step.icon}></i>
              </span>
              <span className="svc-process__num">{pad(i + 1)}</span>
            </div>
            <h3>{step.title}</h3>
            <p>{step.desc}</p>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

export const ServiceUses = ({ label, title, items }) => (
  <section className="s3d-uses">
    <div className="container">
      <div className="s3d-head">
        <span className="s3d-label">{label}</span>
        <h2 className="s3d-heading">{title}</h2>
      </div>
      <div className="row g-3">
        {items.map((item) => (
          <div className="col-lg-4 col-sm-6" key={item.title}>
            <div className="s3d-uses__item">
              <span>
                <i className={item.icon}></i>
              </span>
              <div>
                <h3>{item.title}</h3>
                {item.desc && <p>{item.desc}</p>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Two cards: a light one and a dark one, each with image, heading, text and optional list
export const ServiceWhy = ({ cards }) => (
  <section className="s3d-why">
    <div className="container">
      <div className="row gy-4">
        {cards.map((card, i) => (
          <div className="col-lg-6" key={card.title}>
            <article className={`s3d-why__card${i === 1 ? " s3d-why__card--dark" : ""}`}>
              {card.image && (
                <div className="s3d-why__media">
                  <img src={card.image} alt={card.imageAlt || ""} loading="lazy" />
                </div>
              )}
              <div className="s3d-why__body">
                <span className="s3d-label">{card.label}</span>
                <h2>{card.title}</h2>
                {card.text && <p>{card.text}</p>}
                {card.list && (
                  <ul>
                    {card.list.map((item) => (
                      <li key={item}>
                        <i className="fa-regular fa-check"></i> {item}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </article>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Masonry-style gallery of work (images or clips)
// Gallery item -> viewer project (one image or clip each)
const galleryProject = (item) => ({
  id: item.title,
  name: item.title,
  category: item.tag,
  media: [item.video ? { type: "video", platform: "file", src: item.video, thumbnail: item.poster } : { type: "image", src: item.src, alt: item.title }],
});

// Masonry gallery; with `openable`, clicking an item opens it full screen
export const ServiceGallery = ({ id = "work", label = "Our Work", title, items, dark = false, openable = false }) => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section className={`svc-gallery${dark ? " svc-gallery--dark" : ""}${openable ? " svc-gallery--openable" : ""}`} id={id}>
      <div className="container">
        <div className={`s3d-head${dark ? " s3d-head--light" : ""}`}>
          <span className="s3d-label">{label}</span>
          <h2 className="s3d-heading">{title}</h2>
        </div>
        <div className={`svc-gallery__grid${items.some((item) => item.video) ? " svc-gallery__grid--reels" : ""}${items.length === 3 ? " svc-gallery__grid--three" : ""}`}>
          {items.map((item, i) => (
            <figure className={`svc-gallery__item${item.wide ? " svc-gallery__item--wide" : ""}`} key={item.title}>
              {item.video ? (
                <InViewVideo src={item.video} poster={item.poster} label={item.title} />
              ) : (
                <img src={item.src} alt={item.title} loading="lazy" />
              )}
              <figcaption>
                {item.tag && <span>{item.tag}</span>}
                <strong>{item.title}</strong>
              </figcaption>
              {openable && (
                <button type="button" className="svc-gallery__open" onClick={() => setOpenIndex(i)} aria-label={`Open ${item.title}`}>
                  <i className="fa-regular fa-expand"></i>
                </button>
              )}
            </figure>
          ))}
        </div>
      </div>

      {openIndex !== null && (
        <ProjectViewer projects={items.map(galleryProject)} index={openIndex} onIndexChange={setOpenIndex} onClose={() => setOpenIndex(null)} />
      )}
    </section>
  );
};

// YouTube image helper: "maxresdefault", "oardefault" (Shorts cover) or frames "maxres1".."maxres3" / "hq1".."hq3"
export const ytImage = (id, name) => `https://i.ytimg.com/vi/${id}/${name}.jpg`;

// One video project: cover (plays in place when clicked), optional stills from the video, details
// An item is a YouTube video (`id`) or a local clip (`video` + `poster`)
export const VideoCard = ({ item, playing, onPlay, stills = true }) => (
  <article className={`svc-vwork__item${item.vertical ? " is-vertical" : ""}`}>
    <div className="svc-vwork__screen">
      {playing && item.video ? (
        <video src={item.video} poster={item.poster} controls autoPlay muted loop playsInline></video>
      ) : playing ? (
        <iframe
          src={`https://www.youtube.com/embed/${item.id}?autoplay=1&rel=0&playsinline=1&modestbranding=1`}
          title={item.title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        ></iframe>
      ) : (
        <button type="button" className="svc-vwork__cover" onClick={onPlay} aria-label={`Play ${item.title}`}>
          <img src={item.poster} alt={item.title} loading="lazy" />
          <span className="svc-vwork__play">
            <i className="fa-solid fa-play"></i>
          </span>
        </button>
      )}
    </div>
    {stills && item.stills && (
      <ul className="svc-vwork__stills" aria-label={`Stills from ${item.title}`}>
        {item.stills.map((still, i) => (
          <li key={still}>
            <img src={still} alt={`${item.title}, still ${i + 1}`} loading="lazy" />
          </li>
        ))}
      </ul>
    )}
    <div className="svc-vwork__meta">
      {item.tag && <span className="svc-vwork__tag">{item.tag}</span>}
      <h3>{item.title}</h3>
      {item.desc && <p className="svc-vwork__desc">{item.desc}</p>}
      {item.credit && <p className="svc-vwork__credit">{item.credit}</p>}
      <div className="svc-vwork__links">
        {!item.video && (
          <a href={`https://www.youtube.com/watch?v=${item.id}`} target="_blank" rel="noreferrer">
            <i className="fa-brands fa-youtube"></i> Watch on YouTube
          </a>
        )}
        {(item.links || []).map((link) =>
          link.to ? (
            <Link key={link.to} to={link.to}>
              {link.icon && <i className={link.icon}></i>} {link.label}
            </Link>
          ) : (
            <a key={link.href} href={link.href} target="_blank" rel="noreferrer">
              {link.icon && <i className={link.icon}></i>} {link.label}
            </a>
          )
        )}
      </div>
    </div>
  </article>
);

// Video item -> viewer project (the video, then its stills)
const videoProject = (item) => ({
  id: item.id || item.video,
  name: item.title,
  category: item.tag,
  media: [
    item.video
      ? { type: "video", platform: "file", src: item.video, thumbnail: item.poster }
      : { type: "video", platform: "youtube", src: `https://www.youtube.com/watch?v=${item.id}`, thumbnail: item.poster, vertical: item.vertical },
    ...(item.stills || []).map((src, i) => ({ type: "image", src, alt: `${item.title}, still ${i + 1}` })),
  ],
});

// Video portfolio: each project shows its cover and three stills taken from the video.
// Clicking the cover plays the video in place (one at a time), or with `openable`
// opens the project full screen.
export const ServiceVideoWork = ({ id = "video-work", label = "Video Portfolio", title, items, dark = false, openable = false }) => {
  const [playing, setPlaying] = useState(null);
  const [openIndex, setOpenIndex] = useState(null);
  const vertical = items.every((item) => item.vertical);
  const keyOf = (item) => item.id || item.video;

  return (
    <section className={`svc-vwork${dark ? " svc-vwork--dark" : ""}`} id={id}>
      <div className="container">
        <div className={`s3d-head${dark ? " s3d-head--light" : ""}`}>
          <span className="s3d-label">{label}</span>
          <h2 className="s3d-heading">{title}</h2>
        </div>
        <div className={`svc-vwork__grid${vertical ? " svc-vwork__grid--vertical" : ""}${items.length === 1 ? " svc-vwork__grid--single" : ""}`}>
          {items.map((item, i) => (
            <VideoCard
              key={keyOf(item)}
              item={item}
              playing={!openable && playing === keyOf(item)}
              onPlay={() => (openable ? setOpenIndex(i) : setPlaying(keyOf(item)))}
            />
          ))}
        </div>
      </div>

      {openIndex !== null && (
        <ProjectViewer projects={items.map(videoProject)} index={openIndex} onIndexChange={setOpenIndex} onClose={() => setOpenIndex(null)} />
      )}
    </section>
  );
};

// Social pages we manage: one card per brand (logo, latest posts, link to the page).
// Clicking a post opens that brand's posts full screen.
// A brand: { name, handle, tag, desc, logo, posts: [src] }
export const ServiceSocialWork = ({ id = "work", label = "Our Work", title, brands }) => {
  const [open, setOpen] = useState(null);
  const projects = brands.map((brand) => ({
    id: brand.handle,
    name: brand.name,
    category: brand.tag,
    media: brand.posts.map((src, i) => ({ type: "image", src, alt: `${brand.name} Instagram post ${i + 1}` })),
    link: { href: `https://www.instagram.com/${brand.handle}/`, label: "View on Instagram", icon: "fa-brands fa-instagram" },
  }));

  return (
    <section className="svc-social" id={id}>
      <div className="container">
        <div className="s3d-head">
          <span className="s3d-label">{label}</span>
          <h2 className="s3d-heading">{title}</h2>
        </div>
        <div className="svc-social__grid">
          {brands.map((brand, b) => (
            <article className="svc-social__card" key={brand.handle}>
              <header className="svc-social__head">
                <img className="svc-social__logo" src={brand.logo} alt={`${brand.name} logo`} loading="lazy" />
                <div>
                  <h3>{brand.name}</h3>
                  <span>{brand.tag}</span>
                </div>
              </header>
              <p className="svc-social__desc">{brand.desc}</p>
              <div className="svc-social__posts">
                {brand.posts.map((src, i) => (
                  <button type="button" key={src} onClick={() => setOpen({ index: b, media: i })} aria-label={`Open ${brand.name} post ${i + 1}`}>
                    <img src={src} alt={`${brand.name} Instagram post ${i + 1}`} loading="lazy" />
                  </button>
                ))}
              </div>
              <a className="svc-social__link" href={`https://www.instagram.com/${brand.handle}/`} target="_blank" rel="noreferrer">
                <i className="fa-brands fa-instagram"></i> @{brand.handle}
                <i className="fa-regular fa-arrow-up-right"></i>
              </a>
            </article>
          ))}
        </div>
      </div>

      {open && (
        <ProjectViewer
          projects={projects}
          index={open.index}
          initialMedia={open.media}
          onIndexChange={(index) => setOpen({ index, media: 0 })}
          onClose={() => setOpen(null)}
        />
      )}
    </section>
  );
};
